import { ChangeEvent, useMemo } from 'react';
import { CgDollar } from 'react-icons/cg';

import {
  convertToType,
  divideByOneHundred,
  timesOneHundred,
} from '~/core/application/workflow/utils/transformFuncs';
import {
  FormFieldControlledComponent,
  FormFieldControlledComponentProps,
} from '../../types/fields';
import { NumberInput, NumberInputProps } from './NumberInput.component';

export type CurrencyInputProps = NumberInputProps & {
  defaultToZero?: boolean;
};

/**
 * @deprecated Use block builder with transform functions instead
 *
 * !!!READ ME!!! AVOID DIRECT USAGE IF POSSIBLE
 * CurrencyInput values are always in cents (i.e. $1.00 = 100)
 * so we need to convert to and from dollars when displaying
 * and changing values.
 * current this component should be __AVOIDED__ being used directly
 * and instead use the block builder style, that uses transformFuncsIn/
 * transformFuncsOut to handle the conversion automatically.
 */
export const CurrencyInput: FormFieldControlledComponent<
  string,
  CurrencyInputProps
> = ({
  _ref,
  startElement,
  onChange: _onChange,
  hooks,
  defaultToZero = true,
  ...props
}: FormFieldControlledComponentProps<string, CurrencyInputProps>) => {
  // values are always in cents so convert to dollars:
  const value = useMemo(() => {
    if (
      props.value === undefined ||
      props.value === null ||
      props.value === ''
    ) {
      return '';
    }
    const parsedValue = divideByOneHundred(
      convertToType('number')(`${props.value}`),
    );
    if (defaultToZero || parsedValue > 0) {
      return `${parsedValue}`;
    }
    return '';
  }, [props.value]);

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    // TODO: @haxxxton - we should investigate why preventDefault is needed here
    // also, why it doesnt exist on the ChangeEvent, for "Total sales BAS period 1" in financials input in MO
    event.preventDefault?.();
    const rawValue = event.target.value.replace(/,/g, '');
    const currencyValue = parseFloat(rawValue);
    const normalizedValue = Number.isNaN(currencyValue)
      ? null
      : timesOneHundred(currencyValue);

    if (defaultToZero) {
      _onChange(normalizedValue ?? 0);

      hooks?.afterOnChange?.({
        name: props.name,
        value: normalizedValue ?? 0,
      });
    } else {
      _onChange(normalizedValue ?? '');

      if (normalizedValue && normalizedValue > 0) {
        hooks?.afterOnChange?.({
          name: props.name,
          value: normalizedValue,
        });
      }
    }
  };

  return (
    <NumberInput
      decimalPlaces={2}
      id={props.name}
      onChange={onChange}
      startElement={startElement || <CgDollar />}
      {...props}
      value={value}
    />
  );
};

CurrencyInput.controlled = true;
CurrencyInput.displayName = 'CurrencyInput';
